import { tenantEpoch } from "./tenant-context";
import {
  renewActiveSession,
  renewPermioSession,
  samePermioSession,
  sessionRecentlyActive,
} from "./session";
import { requireAdminMfa } from "./admin-mfa";
import axios, {
  AxiosError,
  CanceledError,
  AxiosHeaders,
  InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "../store/auth.store";
import { ensureCsrfToken, refreshCsrfToken, resetCsrfToken } from "./csrf";

type HttpMethod =
  | "get"
  | "post"
  | "put"
  | "patch"
  | "delete"
  | "head"
  | "options";

const MUTATING_METHODS: HttpMethod[] = ["post", "put", "patch", "delete"];

const configuredApiBaseUrl =
  typeof import.meta.env.VITE_API_BASE_URL === "string"
    ? import.meta.env.VITE_API_BASE_URL.trim()
    : "";
const apiBaseUrl =
  configuredApiBaseUrl.length > 0
    ? configuredApiBaseUrl
    : "https://api.permio.fr/api";

export const http = axios.create({
  baseURL: apiBaseUrl,
  headers: {
    "Content-Type": "application/json",
    // L'adaptateur fetch d'axios pose « User-Agent: axios/x ». Firefox et Safari
    // le déclarent au pré-contrôle CORS, que l'API refuse : la requête ne part pas.
    "User-Agent": false,
  },
  withCredentials: true,
});

import {
  FinancialConfirmationCancelled,
  confirmFinancialAction,
  needsFinancialConfirmation,
} from "./financial-authorization";

let forcedLogoutInProgress = false;

http.interceptors.request.use(async (config) => {
  const epoch = tenantEpoch();
  const scoped = !/\/auth\/(refresh|logout|login|register|forgot-password|reset-password)(?:\?|$)/.test(config.url ?? '');
  const context = config as InternalAxiosRequestConfig & { _tenantEpoch?: number };
  if (scoped && context._tenantEpoch !== undefined && context._tenantEpoch !== epoch) throw new CanceledError('Le contexte a changé.');
  if (scoped) context._tenantEpoch = epoch;
  const entered = useAuthStore.getState();
  const method = (config.method ?? "get").toLowerCase() as HttpMethod;
  if (
    !/\/auth\/(refresh|logout|login|register|forgot-password|reset-password)(?:\?|$)/.test(config.url ?? "")
  ) {
    await renewActiveSession().catch(() => undefined);
    const current = useAuthStore.getState();
    if (!samePermioSession(entered.token, current.token) || entered.activeSchoolId !== current.activeSchoolId) {
      throw new FinancialConfirmationCancelled("La session ou l’école a changé pendant le renouvellement.");
    }
  }
  if (
    needsFinancialConfirmation(method.toUpperCase(), config.url ?? "") &&
    !config.headers?.get("x-fiscal-authorization")
  ) {
    const initial = useAuthStore.getState();
    let payload =
      method === "get"
        ? (config.params ?? {})
        : typeof config.data === "string"
          ? JSON.parse(config.data)
          : (config.data ?? {});
    if (config.data instanceof FormData) {
      const file = config.data.get("file");
      if (!(file instanceof File)) throw new Error("Fichier requis");
      const digest = await crypto.subtle.digest(
        "SHA-256",
        await file.arrayBuffer(),
      );
      const fileHash = Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      payload = { fileHash, contentType: file.type };
      config.headers.set("x-fiscal-file-sha256", fileHash);
      config.headers.set("x-fiscal-file-type", file.type);
    }
    payload = JSON.parse(JSON.stringify(payload));
    if (method === "get")
      payload = Object.fromEntries(
        Object.entries(payload)
          .filter(([, v]) => v !== null)
          .map(([k, v]) => [k, String(v)]),
      );
    const authorization = await confirmFinancialAction(
      method.toUpperCase(),
      config.url!,
      payload,
    );
    const current = useAuthStore.getState();
    if (
      !samePermioSession(current.token, initial.token) ||
      current.activeSchoolId !== initial.activeSchoolId
    )
      throw new FinancialConfirmationCancelled(
        "La session ou l’école a changé. Recommencez la confirmation.",
      );
    config.headers.set("x-fiscal-authorization", authorization);
  }

  if (MUTATING_METHODS.includes(method)) {
    const token = await ensureCsrfToken();
    if (token) {
      let headers: AxiosHeaders;
      if (config.headers instanceof AxiosHeaders) {
        headers = config.headers;
      } else {
        headers = new AxiosHeaders(config.headers ?? {});
        config.headers = headers;
      }
      headers.set("x-csrf-token", token);
    }
  }

  if (scoped && epoch !== tenantEpoch()) throw new CanceledError('Le contexte a changé.');
  const token = useAuthStore.getState().token;
  if (token) {
    let headers: AxiosHeaders;

    if (config.headers instanceof AxiosHeaders) {
      headers = config.headers;
    } else {
      headers = new AxiosHeaders(config.headers ?? {});
      config.headers = headers;
    }

    headers.set("Authorization", `Bearer ${token}`);
  }

  const activeSchoolId = useAuthStore.getState().activeSchoolId;
  if (activeSchoolId) {
    let headers: AxiosHeaders;
    if (config.headers instanceof AxiosHeaders) {
      headers = config.headers;
    } else {
      headers = new AxiosHeaders(config.headers ?? {});
      config.headers = headers;
    }
    headers.set("x-school-id", activeSchoolId);
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const epoch = (response.config as InternalAxiosRequestConfig & { _tenantEpoch?: number })._tenantEpoch;
    if (epoch !== undefined && epoch !== tenantEpoch()) throw new CanceledError('Réponse d’un ancien contexte ignorée.');
    return response;
  },
  async (error: AxiosError) => {
    const epoch = (error.config as (InternalAxiosRequestConfig & { _tenantEpoch?: number }) | undefined)?._tenantEpoch;
    if (epoch !== undefined && epoch !== tenantEpoch()) throw new CanceledError('Réponse d’un ancien contexte ignorée.');
    const status = error.response?.status;
    const message =
      typeof error.response?.data === "object" && error.response?.data !== null
        ? (error.response?.data as Record<string, unknown>).message
        : undefined;

    const errorData =
      typeof error.response?.data === "object" && error.response?.data !== null
        ? (error.response.data as Record<string, unknown>)
        : {};
    if (
      status === 403 &&
      errorData.code === "ADMIN_MFA_REQUIRED" &&
      error.config
    ) {
      if ((error.config as any)._mfaRetried) throw error;
      (error.config as any)._mfaRetried = true;
      await requireAdminMfa();
      return http.request(error.config);
    }
    const reconnectRequiredByCode =
      errorData.code === "RDVPERMIS_RECONNECT_REQUIRED";
    const reconnectRequiredByMessage =
      message ===
        "Impossible de rafraichir l'acces RdvPermis. Reconnectez votre compte." ||
      message ===
        "Connexion RdvPermis expiree. Reconnectez votre compte pour continuer.";
    const reconnectRequired =
      reconnectRequiredByCode ||
      ((status === 401 || status === 403) && reconnectRequiredByMessage);

    const requestUrl = String(error.config?.url ?? "");
    const isLogoutCall = requestUrl.includes("/v1/auth/logout");
    const isRefreshCall = requestUrl.includes("/v1/auth/refresh");

    // L’expiration RDVPermis n’invalide jamais la session Permio.
    if (reconnectRequired)
      window.dispatchEvent(new Event("permio:rdv-reconnect-required"));

    if (
      status === 401 &&
      !reconnectRequired &&
      !isLogoutCall &&
      !isRefreshCall &&
      !/\/auth\/(login|register)(?:\?|$)/.test(requestUrl) &&
      error.config
    ) {
      const config = error.config as InternalAxiosRequestConfig & {
        _refreshRetry?: boolean;
      };
      if (!config._refreshRetry) {
        config._refreshRetry = true;
        const initial = useAuthStore.getState();
        const rejected = String(
          config.headers?.get("Authorization") ?? "",
        ).replace(/^Bearer /, "");
        if (initial.refreshToken && !sessionRecentlyActive())
          return Promise.reject(error);
        if (initial.refreshToken) {
          await renewPermioSession(rejected);
          const current = useAuthStore.getState();
          if (
            current.token &&
            samePermioSession(rejected || null, current.token) &&
            (config.headers.get("x-school-id") ?? null) === current.activeSchoolId &&
            current.activeSchoolId === initial.activeSchoolId
          ) {
            config.headers.set("Authorization", "Bearer " + current.token);
            return http.request(config);
          }
        } else if (!forcedLogoutInProgress) {
          forcedLogoutInProgress = true;
          try {
            await useAuthStore.getState().logout();
          } finally {
            forcedLogoutInProgress = false;
          }
        }
      }
    }

    if (
      status === 403 &&
      message === "invalid csrf token" &&
      error.config &&
      !(error.config as InternalAxiosRequestConfig & { _csrfRetry?: boolean })
        ._csrfRetry
    ) {
      resetCsrfToken();
      const token = await refreshCsrfToken();
      if (token) {
        const config = error.config as InternalAxiosRequestConfig & {
          _csrfRetry?: boolean;
        };
        config._csrfRetry = true;

        let headers: AxiosHeaders;
        if (config.headers instanceof AxiosHeaders) {
          headers = config.headers;
        } else {
          headers = new AxiosHeaders(config.headers ?? {});
          config.headers = headers;
        }
        headers.set("x-csrf-token", token);

        return http.request(config);
      }
    }

    if (typeof message === "string" && message) {
      error.message = message;
    }
    return Promise.reject(error);
  },
);
